<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'

import BaseButton from './BaseButton.vue'
import BaseIcon from './BaseIcon.vue'

/**
 * A right-hand panel for create/edit, 480 px, over a scrim.
 *
 * Why a drawer and not a page: an operator editing a role wants the list still visible —
 * which other roles exist, what they are named — and a full-page form takes that away.
 *
 * Accessibility that is not optional: `role="dialog"` + `aria-modal`, labelled by its
 * title; focus moves INTO the panel on open and BACK to the opener on close; Escape closes.
 * The keydown listener is removed on unmount — the symptom of forgetting that is the next
 * Escape closing a drawer that is not there.
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    subtitle?: string | undefined
    /** `bottom` is the mobile sheet: same dialog semantics, slides up, capped at 85% of the viewport. */
    side?: 'right' | 'bottom'
    /**
     * `wide` is 42 rem, for a panel whose content is a document rather than a form — a JSON
     * payload with an inlined SVG is unreadable in 30 rem however thin the scrollbar is.
     * It is a deliberate two-step scale and not a free width: a drawer that can be any size
     * becomes a different width on every screen, and the list behind it is the reason a
     * drawer was chosen over a page, so it must stay visible.
     */
    size?: 'default' | 'wide'
  }>(),
  { subtitle: undefined, side: 'right', size: 'default' },
)

const emit = defineEmits<{ close: [] }>()

const titleId = useId()
const panel = ref<HTMLElement | null>(null)
const body = ref<HTMLElement | null>(null)
let opener: Element | null = null

const FOCUSABLE = 'input, select, textarea, button, [tabindex="0"]'

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') emit('close')
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      opener = document.activeElement
      document.addEventListener('keydown', onKeydown)
      await nextTick()
      // The first control in the BODY, then any in the panel, else the panel itself. The
      // header's close button is first in DOM order, and landing on it opened every form
      // drawer with focus on the X rather than on the first field.
      const first = body.value?.querySelector<HTMLElement>(FOCUSABLE) ?? panel.value?.querySelector<HTMLElement>(FOCUSABLE)
      ;(first ?? panel.value)?.focus()
    } else {
      document.removeEventListener('keydown', onKeydown)
      if (opener instanceof HTMLElement) opener.focus()
    }
  },
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="drawer">
      <div
        v-if="open"
        :class="[
          'fixed inset-0 z-40 flex backdrop-blur-sm',
          side === 'bottom' ? 'items-end' : 'justify-end',
        ]"
        :data-side="side"
      >
        <!-- The scrim is a button, not a div with a click handler: it must be reachable and announced. -->
        <button
          type="button"
          class="absolute inset-0 bg-rail/40"
          aria-label="Close panel"
          tabindex="-1"
          @click="emit('close')"
        />
        <section
          ref="panel"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          tabindex="-1"
          :class="[
            'relative flex w-full flex-col bg-raised shadow-modal outline-none',
            side === 'bottom' ? 'max-h-[85dvh] rounded-t-lg pb-safe' : 'h-full',
            side === 'bottom' ? '' : size === 'wide' ? 'max-w-[42rem]' : 'max-w-[30rem]',
          ]"
        >
          <!-- The grab handle every phone user expects on a sheet. Decorative: the close button is the control. -->
          <span
            v-if="side === 'bottom'"
            class="mx-auto mt-2 block h-1 w-10 shrink-0 rounded-full bg-border-default"
            aria-hidden="true"
          />
          <header class="flex items-start justify-between gap-4 border-b border-border-subtle px-4 py-3 sm:px-6 sm:py-4">
            <div class="min-w-0">
              <h2
                :id="titleId"
                class="text-title text-ink"
              >
                {{ title }}
              </h2>
              <p
                v-if="subtitle !== undefined"
                class="text-caption text-ink-muted"
              >
                {{ subtitle }}
              </p>
            </div>
            <BaseButton
              variant="ghost"
              aria-label="Close"
              @click="emit('close')"
            >
              <BaseIcon
                name="x-circle"
                :size="18"
              />
            </BaseButton>
          </header>

          <div
            ref="body"
            class="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5"
          >
            <slot />
          </div>

          <footer
            v-if="$slots.footer"
            class="flex items-center justify-end gap-2 border-t border-border-subtle bg-subtle px-4 py-3 sm:px-6"
          >
            <slot name="footer" />
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/*
  The panel travels its OWN WIDTH, not 16 px.

  The old distance was a nudge: at 16 px the panel is already almost everywhere it is
  going when it becomes visible, so the eye reads a fade with a wobble and gets no sense
  of where the thing came from. Covering the full width is the only version that says
  "this slid in from the right", which is the whole reason a drawer is a drawer and not a
  dialog — the list it is covering is still there, just behind.

  **The root's duration has to be the longest one here.** Vue times the transition from
  the computed `transition-duration` of the ROOT element and strips the classes when it
  expires; a root at `--motion-overlay` (160 ms) over a panel at `--motion-panel` (220 ms)
  cuts the slide off 60 ms early and the panel jumps the last third of the way. Both sides
  are driven off the same token for that reason, and the leave off the same shorter one.
*/
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity var(--motion-panel) var(--motion-ease);
}
.drawer-leave-active {
  transition-duration: var(--motion-panel-out);
}

/*
  Decelerate-only on the way in: something arriving from off-screen should enter fast and
  settle, not ease at both ends — an ease-in on an entrance looks like hesitation. On the
  way out the standard curve is right, because the panel is not arriving anywhere.
*/
.drawer-enter-active section {
  transition: transform var(--motion-panel) var(--motion-ease-out);
}
.drawer-leave-active section {
  transition: transform var(--motion-panel-out) var(--motion-ease);
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}
.drawer-enter-from section,
.drawer-leave-to section {
  transform: translateX(100%);
}
.drawer-enter-from[data-side='bottom'] section,
.drawer-leave-to[data-side='bottom'] section {
  transform: translateY(100%);
}
</style>
